/**
 * Orders Hooks
 *
 * React hooks for fetching and managing order data.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getOrders,
  getOrder,
  transitionOrder,
  getFileUploadUrl,
  confirmFileUpload,
  deleteFile,
  getFileDownloadUrl,
  sendApproval,
  resendApproval,
  cancelApproval,
  manualApprove,
  deleteOrder,
} from '../api';
import type {
  OrderListQuery,
  OrderListResponse,
  OrderDetailResponse,
  TransitionRequest,
  FileUploadRequest,
  FileType,
  WorkflowStage,
  TransitionErrorResponse,
} from '../types';
import { ApiClientError } from '../api';

// ============================================
// Hook Types
// ============================================

interface UseOrdersResult {
  data: OrderListResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setQuery: (query: OrderListQuery) => void;
}

interface UseOrderDetailResult {
  data: OrderDetailResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseOrderActionsResult {
  transition: (toStage: WorkflowStage, notes?: string) => Promise<boolean>;
  uploadFile: (file: File, fileType: FileType) => Promise<boolean>;
  removeFile: (fileId: string) => Promise<boolean>;
  downloadFile: (fileId: string, filename: string) => Promise<void>;
  sendApprovalEmail: (proofFileId: string, notes?: string) => Promise<boolean>;
  resendApprovalEmail: (approvalId: string) => Promise<boolean>;
  cancelApprovalEmail: (approvalId: string) => Promise<boolean>;
  approveManually: (notes?: string) => Promise<boolean>;
  removeOrder: () => Promise<boolean>;
  loading: boolean;
  error: string | null;
  transitionError: TransitionErrorResponse | null;
  clearError: () => void;
}

// ============================================
// useOrders Hook
// ============================================

export function useOrders(initialQuery?: OrderListQuery): UseOrdersResult {
  const [data, setData] = useState<OrderListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState<OrderListQuery>(initialQuery || {});

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getOrders(query);
      setData(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch orders';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    data,
    loading,
    error,
    refetch: fetchOrders,
    setQuery,
  };
}

// ============================================
// useOrderDetail Hook
// ============================================

export function useOrderDetail(orderId: string | undefined): UseOrderDetailResult {
  const [data, setData] = useState<OrderDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await getOrder(orderId);
      setData(response);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch order';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return {
    data,
    loading,
    error,
    refetch: fetchOrder,
  };
}

// ============================================
// useOrderActions Hook
// ============================================

export function useOrderActions(
  orderId: string,
  onSuccess?: () => void
): UseOrderActionsResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transitionError, setTransitionError] = useState<TransitionErrorResponse | null>(null);

  const handleError = (err: unknown) => {
    const message = err instanceof ApiClientError ? err.message : 'Operation failed';
    setError(message);
  };

  const clearError = useCallback(() => {
    setError(null);
    setTransitionError(null);
  }, []);

  const transition = useCallback(
    async (toStage: WorkflowStage, notes?: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      setTransitionError(null);
      try {
        const request: TransitionRequest = { toStage, notes };
        await transitionOrder(orderId, request);
        onSuccess?.();
        return true;
      } catch (err) {
        if (err instanceof ApiClientError && err.data) {
          // Capture the full transition error data for detailed display
          setTransitionError(err.data as TransitionErrorResponse);
        }
        handleError(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [orderId, onSuccess]
  );

  const uploadFile = useCallback(
    async (file: File, fileType: FileType): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        // Browsers return empty string for unknown file types (e.g. .dst, .pes, .jef)
        // Fall back to application/octet-stream so the API and signed URL work correctly
        const mimeType = file.type || 'application/octet-stream';

        const request: FileUploadRequest = {
          filename: file.name,
          mimeType,
          fileType,
        };

        // Get signed upload URL
        const { uploadUrl, fileId } = await getFileUploadUrl(orderId, request);

        // Upload file to signed URL
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': mimeType },
          body: file,
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text().catch(() => '');
          console.error('File upload failed:', uploadResponse.status, uploadResponse.statusText, errorText);
          throw new Error(`Failed to upload file: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        // Confirm upload with file size
        await confirmFileUpload(orderId, fileId, file.size);
        onSuccess?.();
        return true;
      } catch (err) {
        handleError(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [orderId, onSuccess]
  );

  const removeFile = useCallback(
    async (fileId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await deleteFile(orderId, fileId);
        onSuccess?.();
        return true;
      } catch (err) {
        handleError(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [orderId, onSuccess]
  );

  const downloadFile = useCallback(
    async (fileId: string, filename: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const { downloadUrl } = await getFileDownloadUrl(orderId, fileId);

        // Fetch as blob to force download (cross-origin URLs ignore the download attribute)
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    },
    [orderId]
  );

  const sendApprovalEmail = useCallback(
    async (proofFileId: string, notes?: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await sendApproval(orderId, { proofFileId, notes });
        onSuccess?.();
        return true;
      } catch (err) {
        handleError(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [orderId, onSuccess]
  );

  const resendApprovalEmail = useCallback(
    async (approvalId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await resendApproval(orderId, approvalId);
        onSuccess?.();
        return true;
      } catch (err) {
        handleError(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [orderId, onSuccess]
  );

  const cancelApprovalEmail = useCallback(
    async (approvalId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await cancelApproval(orderId, approvalId);
        onSuccess?.();
        return true;
      } catch (err) {
        handleError(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [orderId, onSuccess]
  );

  const approveManually = useCallback(
    async (notes?: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await manualApprove(orderId, notes);
        onSuccess?.();
        return true;
      } catch (err) {
        handleError(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [orderId, onSuccess]
  );

  const removeOrder = useCallback(
    async (): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await deleteOrder(orderId);
        return true;
      } catch (err) {
        handleError(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [orderId]
  );

  return {
    transition,
    uploadFile,
    removeFile,
    downloadFile,
    sendApprovalEmail,
    resendApprovalEmail,
    cancelApprovalEmail,
    approveManually,
    removeOrder,
    loading,
    error,
    transitionError,
    clearError,
  };
}
